import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardSettings } from '@/pages/dashboard/types';
import { formatTimeForDisplay, getProgressColor } from '@/pages/dashboard/utils';
import { statsApi } from '@/services/statistics';
import { useAuth } from '@/hooks/useAuth';
import { useHideAmounts } from '@/hooks/useHideAmounts';

interface TeamStatisticsProps {
  settings: DashboardSettings;
}

interface TeamMemberStats {
  user_info: {
    id: number;
    name: string;
    email: string;
    firm: string;
  };
  summary: {
    total_hours_actual: number;
    billable_hours_actual: number;
    utilization_rate: number;
    total_revenue_actual: number;
  };
  targets: {
    yearly_target: number;
    ytd_billable_hours: number;
    target_progress_percentage: number;
    hours_ahead_behind: number;
    monthly_target: number;
  };
}

interface TeamOverviewData {
  team_summary: {
    total_members: number;
    total_revenue: number;
    total_billable_hours: number;
    average_utilization: number;
  };
  team_members: TeamMemberStats[];
}

export function TeamStatistics({
  settings
}: TeamStatisticsProps) {
  const { user } = useAuth();
  const { hideAmounts, amountClassName } = useHideAmounts();
  const [teamData, setTeamData] = useState<TeamOverviewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch team overview data - no reference_date, backend uses firm's defaults
        const response = await statsApi.getTeamOverview({
          period: 'year'
        });
        
        console.log('Team overview response:', response);
        
        // Cast the response to our expected type
        const processedData = response as unknown as TeamOverviewData;
        
        setTeamData(processedData);
      } catch (err) {
        console.error('Error fetching team statistics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load team statistics');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchTeamData();
    }
  }, [user]);

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className={settings.compactMode ? 'text-base' : 'text-lg'}>
            Team Target Progress
          </CardTitle>
          <div className={`text-muted-foreground ${settings.compactMode ? 'text-xs' : 'text-sm'}`}>
            Track how each team member is performing against their yearly targets
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className={settings.compactMode ? 'text-base' : 'text-lg'}>
            Team Target Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-sm">
            Error loading team statistics: {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!teamData?.team_members || teamData.team_members.length === 0) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className={settings.compactMode ? 'text-base' : 'text-lg'}>
            Team Target Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-sm">
            No team data available. {teamData ? 'Team members array is empty.' : 'No team data received.'}
          </div>
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-muted-foreground">Debug Info</summary>
              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto">
                {JSON.stringify(teamData, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className={settings.compactMode ? 'text-base' : 'text-lg'}>
          Team Target Progress
        </CardTitle>
        <div className={`text-muted-foreground ${settings.compactMode ? 'text-xs' : 'text-sm'}`}>
          Track how each team member is performing against their yearly targets
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className={settings.compactMode ? 'h-[300px] pr-4' : 'h-[400px] pr-4'}>
          <div className="space-y-6">
            {teamData.team_members
              .filter((member: TeamMemberStats) => 
                // Exclude members with zero billable hours
                (member.summary.billable_hours_actual || 0) > 0
              )
              .sort((a: TeamMemberStats, b: TeamMemberStats) => {
                // Use target_progress_percentage from API - already accounts for period progress
                const aProgress = a.targets.target_progress_percentage || 0;
                const bProgress = b.targets.target_progress_percentage || 0;
                return bProgress - aProgress;
              })
              .map((userStat: TeamMemberStats) => {
                // Use target_progress_percentage from API - properly scales with time elapsed
                const targetProgress = Math.round(userStat.targets.target_progress_percentage || 0);
                
                const progressStyle = getProgressColor(targetProgress, settings);
                
                return (
                  <div key={userStat.user_info.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${settings.compactMode ? 'text-sm' : ''}`}>
                          {userStat.user_info.name}
                        </span>
                        {targetProgress >= 125 && (
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                            🏆 Diamond
                          </span>
                        )}
                        {targetProgress >= 105 && targetProgress < 125 && (
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                            🥇 Gold
                          </span>
                        )}
                        {targetProgress >= 100 && targetProgress < 105 && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                            ✅ Target
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className={`text-right ${settings.compactMode ? 'text-xs' : 'text-sm'}`}>
                          <span className="font-medium">
                            {formatTimeForDisplay(userStat.summary.billable_hours_actual || 0, settings)}
                          </span>
                          <span className="text-muted-foreground text-xs ml-1">
                            / {userStat.targets.yearly_target}h
                          </span>
                        </div>
                        <div className={`text-muted-foreground min-w-[40px] text-right ${settings.compactMode ? 'text-xs' : 'text-sm'}`}>
                          {targetProgress}%
                        </div>
                      </div>
                    </div>
                    <div className={`h-3 w-full bg-muted rounded-full overflow-hidden ${settings.compactMode ? 'h-2' : ''}`}>
                      <div 
                        className={`h-full relative z-10`}
                        style={{ 
                          width: `${Math.min(targetProgress, 100)}%`,
                          backgroundColor: progressStyle.color
                        }}
                      ></div>
                    </div>
                    
                    {/* Additional user stats in compact view */}
                    {settings.compactMode && (
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Utilization: {Math.round(userStat.summary.utilization_rate || 0)}%</span>
                        <span>
                          Behind/Ahead: {userStat.targets.hours_ahead_behind >= 0 ? '+' : ''}
                          {formatTimeForDisplay(Math.abs(userStat.targets.hours_ahead_behind), settings)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </ScrollArea>
        
        {/* Team Summary */}
        <div className="mt-6 pt-4 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/10 p-3 rounded-md">
              <div className="text-xs text-muted-foreground">Team Utilization</div>
              <div className={`font-medium mt-1 ${settings.compactMode ? 'text-sm' : 'text-lg'}`}>
                {Math.round(teamData.team_summary.average_utilization || 0)}%
              </div>
            </div>
            <div className="bg-muted/10 p-3 rounded-md">
              <div className="text-xs text-muted-foreground">Total Billable</div>
              <div className={`font-medium mt-1 ${settings.compactMode ? 'text-sm' : 'text-lg'}`}>
                {formatTimeForDisplay(teamData.team_summary.total_billable_hours || 0, settings)}
              </div>
            </div>
            <div className="bg-muted/10 p-3 rounded-md">
              <div className="text-xs text-muted-foreground">Total Members</div>
              <div className={`font-medium mt-1 ${settings.compactMode ? 'text-sm' : 'text-lg'}`}>
                {teamData.team_summary.total_members || 0}
              </div>
            </div>
            <div className="bg-muted/10 p-3 rounded-md">
              <div className="text-xs text-muted-foreground">Total Revenue</div>
              <div className={`font-medium mt-1 ${settings.compactMode ? 'text-sm' : 'text-lg'} ${amountClassName}`}>
                {hideAmounts ? '•••' : `€${Math.round(teamData.team_summary.total_revenue || 0).toLocaleString()}`}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}